import { Image as ImageIcon } from '@phosphor-icons/react/dist/icons/Image';
import { PlayCircle } from '@phosphor-icons/react/dist/icons/PlayCircle';
import clayArch from '../assets/clay-arch.png';
import { META_CTA_LABELS, type MetaCtaType } from '../types';

function PreviewLabel({ children }: { children: React.ReactNode }) {
  return <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-content-tertiary">{children}</div>;
}

function Avatar({ rounded }: { rounded: 'full' | 'md' }) {
  return (
    <span
      className={`flex size-9 shrink-0 items-center justify-center overflow-hidden border border-border-primary bg-bg-primary ${
        rounded === 'full' ? 'rounded-full' : 'rounded-md'
      }`}
    >
      <img src={clayArch} alt="" className="w-6" />
    </span>
  );
}

export interface MetaFeedPreviewContent {
  primaryText: string;
  headline: string;
  description: string;
  callToAction: MetaCtaType | null;
  destinationUrl: string;
  imageUrl: string | null;
  /** When set, the creative is a video and imageUrl is its poster frame. */
  videoUrl?: string | null;
}

function displayDomain(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return 'clay.com';
  }
}

export function MetaFeedPreview({
  content,
  label = 'Feed preview',
  imageInputId,
}: {
  content: MetaFeedPreviewContent;
  label?: string;
  imageInputId?: string;
}) {
  const ctaLabel = content.callToAction ? META_CTA_LABELS[content.callToAction] : null;

  return (
    <div className="flex flex-col gap-2.5">
      <PreviewLabel>{label}</PreviewLabel>
      <div className="overflow-hidden rounded-xl border border-border-primary bg-bg-primary shadow-sm">
        <div className="flex items-center gap-2.5 p-3">
          <Avatar rounded="full" />
          <div className="flex flex-col">
            <span className="text-sm font-semibold">Clay</span>
            <span className="text-xs text-content-tertiary">Sponsored</span>
          </div>
        </div>
        <p className="whitespace-pre-line px-3 pb-3 text-sm leading-snug">{content.primaryText}</p>
        {content.videoUrl ? (
          <div className="relative bg-black">
            <video
              src={content.videoUrl}
              poster={content.imageUrl ?? undefined}
              controls
              muted
              playsInline
              loop
              className="max-h-[480px] w-full object-contain"
            />
            {imageInputId && (
              <label
                htmlFor={imageInputId}
                className="absolute right-2 top-2 cursor-pointer rounded-md bg-bg-primary/90 px-2 py-1 text-xs font-medium"
              >
                Replace
              </label>
            )}
          </div>
        ) : content.imageUrl ? (
          imageInputId ? (
            <label htmlFor={imageInputId} className="group relative block cursor-pointer" title="Replace image">
              <img src={content.imageUrl} alt="Ad creative" className="aspect-square w-full object-cover" />
              <span className="absolute inset-x-0 bottom-0 bg-bg-primary/80 py-1.5 text-center text-xs font-medium opacity-0 transition-opacity group-hover:opacity-100">
                Click or drop to replace
              </span>
            </label>
          ) : (
            <img src={content.imageUrl} alt="Ad creative" className="aspect-square w-full object-cover" />
          )
        ) : (
          <label
            htmlFor={imageInputId}
            className={`flex aspect-square flex-col items-center justify-center gap-1.5 bg-bg-purple-light text-content-secondary ${
              imageInputId ? 'cursor-pointer hover:opacity-90' : ''
            }`}
          >
            <ImageIcon size={28} />
            <span className="text-sm font-medium">Creative · 1:1</span>
            <span className="text-xs">{imageInputId ? 'Click or drop an image or video' : 'No image'}</span>
          </label>
        )}
        <div className="flex items-center gap-3 bg-bg-secondary px-3 py-2.5">
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-[11px] uppercase tracking-wide text-content-tertiary">
              {displayDomain(content.destinationUrl)}
            </span>
            <span className="text-sm font-semibold">{content.headline}</span>
            <span className="text-xs text-content-tertiary">{content.description}</span>
          </div>
          {ctaLabel && <span className="shrink-0 rounded-md bg-bg-tertiary px-3 py-1.5 text-sm font-medium">{ctaLabel}</span>}
        </div>
      </div>
    </div>
  );
}

export function LinkedInPostPreview({
  post,
  loading,
  problem,
}: {
  post: { commentary: string; mediaType: string; imageUrl: string | null } | null;
  loading: boolean;
  problem: string | null;
}) {
  return (
    <div className="flex flex-col gap-2.5">
      <PreviewLabel>Boosted post preview</PreviewLabel>
      <div className="overflow-hidden rounded-xl border border-border-primary bg-bg-primary shadow-sm">
        <div className="flex items-center gap-2.5 p-3">
          <Avatar rounded="md" />
          <div className="flex flex-col">
            <span className="text-sm font-semibold">Clay</span>
            <span className="text-xs text-content-tertiary">Promoted</span>
          </div>
        </div>
        {post ? (
          <>
            <p className="line-clamp-6 whitespace-pre-line px-3 pb-3 text-sm leading-snug">{post.commentary || 'No text on this post.'}</p>
            {post.imageUrl ? (
              <img src={post.imageUrl} alt="Post media" className="w-full object-cover" />
            ) : (
              <div className="flex aspect-video flex-col items-center justify-center gap-1.5 bg-bg-blue-light text-content-secondary">
                {post.mediaType === 'video' ? <PlayCircle size={28} /> : <ImageIcon size={28} />}
                <span className="text-sm font-medium capitalize">{post.mediaType === 'none' ? 'Text post' : post.mediaType}</span>
              </div>
            )}
          </>
        ) : (
          <div className="flex aspect-video flex-col items-center justify-center gap-1.5 bg-bg-blue-light px-6 text-center text-content-secondary">
            <ImageIcon size={28} />
            <span className="text-sm">
              {loading ? 'Loading the post from LinkedIn…' : (problem ?? 'Paste a Clay company post link to see it here.')}
            </span>
          </div>
        )}
        <div className="border-t border-border-primary px-3 py-2.5 text-xs text-content-tertiary">
          The post's own copy and media are used; they can't be edited when boosting.
        </div>
      </div>
    </div>
  );
}

/** Stories and Reels: the 9:16 asset full-bleed, with the copy and button over its lower part. */
export function MetaStoryPreview({
  content,
  label = 'Stories & Reels preview',
  imageInputId,
}: {
  content: MetaFeedPreviewContent;
  label?: string;
  imageInputId?: string;
}) {
  const ctaLabel = content.callToAction ? META_CTA_LABELS[content.callToAction] : 'Learn more';
  return (
    <div className="flex flex-col gap-2.5">
      <PreviewLabel>{label}</PreviewLabel>
      <div className="relative mx-auto aspect-[9/16] w-64 overflow-hidden rounded-xl border border-border-primary bg-black shadow-sm">
        {content.videoUrl ? (
          <video
            src={content.videoUrl}
            poster={content.imageUrl ?? undefined}
            muted
            playsInline
            loop
            autoPlay
            className="size-full object-cover"
          />
        ) : content.imageUrl ? (
          <img src={content.imageUrl} alt="Ad creative, 9:16" className="size-full object-cover" />
        ) : (
          <label
            htmlFor={imageInputId}
            className={`flex size-full flex-col items-center justify-center gap-1.5 bg-bg-purple-light text-content-secondary ${
              imageInputId ? 'cursor-pointer hover:opacity-90' : ''
            }`}
          >
            <ImageIcon size={28} />
            <span className="text-sm font-medium">Creative · 9:16</span>
            <span className="px-6 text-center text-xs">
              {imageInputId ? 'Click or drop the vertical image or video' : 'No 9:16 version; Meta crops the feed one'}
            </span>
          </label>
        )}
        <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center gap-2 bg-gradient-to-b from-black/60 to-transparent p-3 text-white">
          <Avatar rounded="full" />
          <div className="flex flex-col">
            <span className="text-sm font-semibold">Clay</span>
            <span className="text-xs opacity-80">Sponsored</span>
          </div>
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col gap-2 bg-gradient-to-t from-black/70 to-transparent p-3 text-white">
          <p className="line-clamp-2 text-xs leading-snug">{content.primaryText}</p>
          <span className="self-center rounded-full bg-white px-4 py-1.5 text-xs font-semibold text-black">{ctaLabel}</span>
        </div>
        {imageInputId && (content.imageUrl || content.videoUrl) && (
          <label
            htmlFor={imageInputId}
            className="absolute right-2 top-14 cursor-pointer rounded-md bg-bg-primary/90 px-2 py-1 text-xs font-medium"
          >
            Replace
          </label>
        )}
      </div>
    </div>
  );
}
